import React, { useState, useEffect } from 'react';
import { Users, FolderOpen, Shield, Plus, Trash2, Edit, Check, X, AlertCircle } from 'lucide-react';

const PermissionsManager = () => {
  const [users, setUsers] = useState([]);
  const [folders, setFolders] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionTypes, setPermissionTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load users
      const usersResponse = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users);
      }
      
      // Load folder structure
      const foldersResponse = await fetch('/api/pdf/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (foldersResponse.ok) {
        const foldersData = await foldersResponse.json();
        setFolders(extractFolders(foldersData.tree));
      }
      
      // Load permissions
      const permissionsResponse = await fetch('/api/permissions/all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (permissionsResponse.ok) {
        const permissionsData = await permissionsResponse.json();
        setPermissions(permissionsData.permissions);
      }
      
      // Load permission types
      const typesResponse = await fetch('/api/permissions/types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (typesResponse.ok) {
        const typesData = await typesResponse.json();
        setPermissionTypes(typesData.descriptions);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Hiba történt az adatok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  // Extract folders from tree structure
  const extractFolders = (tree) => {
    const folders = [];
    
    // Add root folder
    folders.push({ path: '', name: 'Gyökér mappa', displayPath: '/' });
    
    const processNode = (node, parentPath = '') => {
      if (node.type === 'folder') {
        const folderPath = parentPath ? `${parentPath}/${node.name}` : node.name;
        folders.push({
          path: folderPath,
          name: node.name,
          displayPath: `/${folderPath}`
        });
        
        // Process children
        if (node.children) {
          node.children.forEach(child => processNode(child, folderPath));
        }
      }
    };
    
    tree.forEach(node => processNode(node));
    return folders;
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.username} (${user.role})` : `User ${userId}`;
  };

  // Get folder name by path
  const getFolderName = (folderPath) => {
    if (!folderPath) return 'Gyökér mappa';
    const folder = folders.find(f => f.path === folderPath);
    return folder ? folder.displayPath : folderPath;
  };

  // Handle add permission
  const handleAddPermission = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !selectedFolder || selectedPermissions.length === 0) {
      setError('Válassz felhasználót, mappát és legalább egy jogosultságot');
      return;
    }
    
    try {
      const response = await fetch('/api/permissions/set', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          userId: parseInt(selectedUser),
          folderId: Buffer.from(selectedFolder).toString('base64'),
          permissions: selectedPermissions
        })
      });
      
      if (response.ok) {
        setSuccess('Jogosultság sikeresen beállítva');
        setSelectedUser('');
        setSelectedFolder('');
        setSelectedPermissions([]);
        setShowAddForm(false);
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Hiba történt a jogosultság beállítása során');
      }
    } catch (error) {
      console.error('Error adding permission:', error);
      setError('Hiba történt a jogosultság beállítása során');
    }
  };

  // Handle remove permission
  const handleRemovePermission = async (permission) => {
    if (!window.confirm('Biztosan törlöd ezt a jogosultságot?')) {
      return;
    }
    
    try {
      const response = await fetch('/api/permissions/remove', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          userId: permission.userId,
          folderId: Buffer.from(permission.folderPath).toString('base64')
        })
      });
      
      if (response.ok) {
        setSuccess('Jogosultság sikeresen törölve');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Hiba történt a jogosultság törlése során');
      }
    } catch (error) {
      console.error('Error removing permission:', error);
      setError('Hiba történt a jogosultság törlése során');
    }
  };

  // Handle edit permission
  const handleEditPermission = (permission) => {
    setEditingPermission(permission);
    setSelectedUser(permission.userId.toString());
    setSelectedFolder(permission.folderPath);
    setSelectedPermissions([...permission.permissions]);
    setShowAddForm(true);
  };

  // Handle update permission
  const handleUpdatePermission = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !selectedFolder || selectedPermissions.length === 0) {
      setError('Válassz felhasználót, mappát és legalább egy jogosultságot');
      return;
    }
    
    try {
      const response = await fetch('/api/permissions/set', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          userId: parseInt(selectedUser),
          folderId: Buffer.from(selectedFolder).toString('base64'),
          permissions: selectedPermissions
        })
      });
      
      if (response.ok) {
        setSuccess('Jogosultság sikeresen módosítva');
        setSelectedUser('');
        setSelectedFolder('');
        setSelectedPermissions([]);
        setShowAddForm(false);
        setEditingPermission(null);
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Hiba történt a jogosultság módosítása során');
      }
    } catch (error) {
      console.error('Error updating permission:', error);
      setError('Hiba történt a jogosultság módosítása során');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingPermission(null);
    setSelectedUser('');
    setSelectedFolder('');
    setSelectedPermissions([]);
    setShowAddForm(false);
  };

  // Handle permission type change
  const handlePermissionTypeChange = (permissionType) => {
    if (selectedPermissions.includes(permissionType)) {
      setSelectedPermissions(selectedPermissions.filter(p => p !== permissionType));
    } else {
      setSelectedPermissions([...selectedPermissions, permissionType]);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Jogosultság Kezelő</h2>
        <p className="text-gray-600">Felhasználók mappa szintű hozzáférésének kezelése</p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center">
          <Check className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {/* Add Permission Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Új Jogosultság Hozzáadása
        </button>
      </div>

      {/* Add/Edit Permission Form */}
      {showAddForm && (
        <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-lg font-medium mb-4">
            {editingPermission ? 'Jogosultság Módosítása' : 'Új Jogosultság Hozzáadása'}
          </h3>
          
          <form onSubmit={editingPermission ? handleUpdatePermission : handleAddPermission} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Felhasználó
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Válassz felhasználót...</option>
                  {users.filter(user => user.role !== 'admin').map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mappa
                </label>
                <select
                  value={selectedFolder}
                  onChange={(e) => setSelectedFolder(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Válassz mappát...</option>
                  {folders.map(folder => (
                    <option key={folder.path} value={folder.path}>
                      {folder.displayPath}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jogosultságok
              </label>
              <div className="space-y-2">
                {Object.entries(permissionTypes).map(([type, description]) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(type)}
                      onChange={() => handlePermissionTypeChange(type)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{description}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Check className="h-4 w-4 mr-2" />
                {editingPermission ? 'Módosítás' : 'Hozzáadás'}
              </button>
              
              <button
                type="button"
                onClick={handleCancelEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-2" />
                Mégse
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Permissions Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Beállított Jogosultságok
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            A rendszerben beállított mappa jogosultságok listája
          </p>
        </div>
        
        <div className="border-t border-gray-200">
          {permissions.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              <Shield className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nincsenek jogosultságok</h3>
              <p className="mt-1 text-sm text-gray-500">
                Kezdj egy új jogosultság hozzáadásával
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Felhasználó
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mappa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Jogosultságok
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Létrehozva
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Műveletek
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {permissions.map((permission) => (
                    <tr key={permission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {getUserName(permission.userId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FolderOpen className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {getFolderName(permission.folderPath)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {permission.permissions.map((perm) => (
                            <span
                              key={perm}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {permissionTypes[perm]}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(permission.createdAt).toLocaleDateString('hu-HU')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditPermission(permission)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRemovePermission(permission)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsManager; 